import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Copy, ExternalLink, Info } from "lucide-react";
import { Button, Combobox, Dialog, Label, Textarea } from "@/components/ui";
import { Fan, Form, FormRequest, FormServices, FanServices, PlayersServices } from "@/services/bedrock";
import { usePaginatedCombobox, URLS } from "@/utils";
import { cn } from "@/lib/utils";
import { FanCreationForm } from "@/components/Forms/FanCreationForm";
import { SocoAiLogo } from "@/components/Holo";
import { FormPersonalizationHelpDialog } from "./FormPersonalizationHelpDialog";

type Audience = "client" | "player" | "anyone";
type View = "compose" | "personalizing" | "result";

interface PlayerOption {
  id: number;
  label: string;
  name: string;
}
interface FanOption {
  id: string;
  label: string;
  original: Fan;
}
interface FormOption {
  id: number;
  label: string;
  original: Form;
}

interface FormRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The form the link is for. Fan Zone leaves it out and picks one in the dialog. */
  form: Form | null;
  /** Edit this link instead of creating one. */
  formRequest?: FormRequest | null;
  /** Fan Zone: the link is for this Fan, who can't be changed. */
  fan?: Fan | null;
}

const AUDIENCES: { id: Audience; label: string }[] = [
  { id: "client", label: "A client" },
  { id: "player", label: "A player" },
  { id: "anyone", label: "Anyone" },
];

const firstName = (name: string) => name.split(" ")[0];

/**
 * The picker clears a value it can't find among the loaded items, with no user action behind it.
 * A Fan or player set for the user (a new Fan, a Fan's player) can be ahead of the list, so those
 * clears are ignored; the seeded value joins the list right after.
 */
const isProgrammaticClear = (details?: { reason?: string }) => details?.reason === "none";

const playerOption = (player: { id: number; fullName?: string | null; email?: string | null }): PlayerOption => ({
  id: player.id,
  label: `${player.fullName || "No Name"}${player.email ? ` - ${player.email}` : ""}`,
  name: player.fullName || player.email || "this player",
});

const fanOption = (fan: Fan): FanOption => ({
  id: fan.id,
  label: `${fan.displayName || "No Name"}${fan.email ? ` - ${fan.email}` : ""}`,
  original: fan,
});

/** A form opens on "Anyone" when it doesn't collect contact info, since it isn't about a person. */
const defaultAudience = (form: Form | null): Audience =>
  form?.fanIdentityCollection === "hidden" ? "anyone" : "client";

function plainLinkError(error: unknown, playerName?: string) {
  const message = typeof error === "string" ? error : "";
  if (/not eligible/i.test(message))
    return `${playerName ?? "This player"} can't use this form. Check the form's eligibility.`;
  return "We couldn't load the link. Try again.";
}

function LinkBox({ url, caption }: { url: string; caption?: string }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy. Select the link and copy it instead.");
    }
  };

  return (
    <div className="flex flex-col gap-2 rounded-xl border border-slate-200 p-3" data-testid="form-link-box">
      <div className="flex flex-wrap items-center gap-2">
        <span className="min-w-0 flex-1 basis-64 select-all break-all rounded-lg bg-slate-50 px-2.5 py-2 font-mono text-xs text-slate-700">
          {url}
        </span>
        <Button size="sm" onClick={copy} type="button">
          <Copy className="size-3.5" />
          Copy
        </Button>
        <Button
          size="sm"
          variant="outline"
          type="button"
          render={<a href={url} target="_blank" rel="noopener noreferrer" />}
        >
          <ExternalLink className="size-3.5" />
          Open
        </Button>
      </div>
      {caption && <p className="text-xs text-slate-500">{caption}</p>}
    </div>
  );
}

function Field({ label, htmlFor, children }: { label: string; htmlFor?: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-[5.5rem_minmax(0,1fr)] sm:gap-3">
      <Label htmlFor={htmlFor} className="text-sm font-semibold text-slate-600 sm:pt-2">
        {label}
      </Label>
      <div className="flex min-w-0 flex-col gap-1.5">{children}</div>
    </div>
  );
}

export function FormRequestDialog({ open, onOpenChange, form, formRequest, fan: lockedFan }: FormRequestDialogProps) {
  const queryClient = useQueryClient();
  const history = useHistory();
  const isEdit = !!formRequest;
  const isFanZone = !!lockedFan && !isEdit;

  const [audience, setAudience] = useState<Audience>(defaultAudience(form));
  const [view, setView] = useState<View>("compose");
  const [result, setResult] = useState<FormRequest | null>(null);
  const [playerFromFan, setPlayerFromFan] = useState(false);
  const [newFanOpen, setNewFanOpen] = useState(false);
  const [creatingFan, setCreatingFan] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [useAi, setUseAi] = useState(false);
  const [aiContext, setAiContext] = useState("");
  const [isTest, setIsTest] = useState(false);

  const getPlayers = useCallback(async (search: string | undefined, page: number) => {
    const res = await PlayersServices.getPlayers({ search, page, perPage: 25 });
    return { ...res, content: res.content.map(playerOption) };
  }, []);
  const getFans = useCallback(async (search: string | undefined, page: number) => {
    const res = await FanServices.getFans({ search, page, active: true, perPage: 25 } as any);
    return { ...res, content: res.content.map(fanOption) };
  }, []);
  const getForms = useCallback(async (search: string | undefined, page: number) => {
    const res = await FormServices.getForms({ search, page, active: true, perPage: 25 } as any);
    return {
      ...res,
      content: res.content.map(f => ({ id: f.id, label: f.displayName || "Untitled Form", original: f })),
    };
  }, []);

  // A player set for the user (the link's, the locked Fan's, or a picked Fan's) seeds the list, so
  // the picker recognizes it even before the players have loaded instead of clearing it
  const [seedPlayer, setSeedPlayer] = useState<PlayerOption | null>(null);
  const [seedFan, setSeedFan] = useState<FanOption | null>(null);
  const players = usePaginatedCombobox<PlayerOption>(getPlayers, false, seedPlayer);
  const fans = usePaginatedCombobox<FanOption>(getFans, false, seedFan);
  const forms = usePaginatedCombobox<FormOption>(getForms, false);

  const activeForm: Form | null = form ?? forms.value?.original ?? null;
  const fan: Fan | null = lockedFan ?? formRequest?.fan ?? fans.value?.original ?? null;
  const player = players.value ?? null;
  const allowAi = !!activeForm?.allowAiPersonalization;
  const hasOptions = isTest;
  const forClient = isEdit ? !!formRequest?.fan : isFanZone || audience === "client";

  // Start fresh each time the dialog opens; an edit starts from the link's own settings
  useEffect(() => {
    if (!open) return;
    setView("compose");
    setResult(null);
    setNewFanOpen(false);
    setHelpOpen(false);
    setAudience(isFanZone ? "client" : defaultAudience(form));
    setUseAi(!!formRequest?.useAiPersonalization);
    setAiContext(formRequest?.contextInstructions || "");
    setIsTest(!!formRequest?.isTest);
    setSeedFan(null);
    fans.onValueChange(null, undefined);
    forms.onValueChange(null, undefined);
    const starting = formRequest?.player ?? lockedFan?.player;
    const startingPlayer = starting ? playerOption(starting) : null;
    setSeedPlayer(startingPlayer);
    players.onValueChange(startingPlayer, undefined);
    setPlayerFromFan(!!lockedFan?.player && !formRequest);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form, formRequest, lockedFan]);

  const chooseFan = (option: FanOption | null) => {
    setSeedFan(option);
    fans.onValueChange(option, undefined);
    const fanPlayer = option?.original.player ? playerOption(option.original.player) : null;
    setSeedPlayer(fanPlayer);
    players.onValueChange(fanPlayer, undefined);
    setPlayerFromFan(!!fanPlayer);
  };

  const choosePlayer = (option: PlayerOption | null | undefined) => {
    players.onValueChange(option ?? null, undefined);
    setPlayerFromFan(false);
  };

  const switchAudience = (next: Audience) => {
    setAudience(next);
    setIsTest(false);
    if (next === "player" && playerFromFan) choosePlayer(null);
  };

  // A player's link, or the form's general link, is one link the server reuses every time it's asked
  // for, so asking for it as soon as the player is picked creates it at most once
  const wantsPlainLink =
    open &&
    !isEdit &&
    !isFanZone &&
    view === "compose" &&
    audience !== "client" &&
    !hasOptions &&
    !!activeForm?.id &&
    (audience === "anyone" || !!player);
  const plainLink = useQuery({
    queryKey: ["plainFormLink", activeForm?.id, audience === "player" ? player?.id : null],
    queryFn: () =>
      FormServices.createFormRequest(activeForm!.id, audience === "player" ? { playerId: player!.id } : {}),
    enabled: wantsPlainLink,
    retry: false,
    staleTime: Infinity,
  });
  useEffect(() => {
    if (plainLink.data) queryClient.invalidateQueries({ queryKey: ["formRequests"] });
  }, [plainLink.data, queryClient]);

  const finish = (request: FormRequest) => {
    queryClient.invalidateQueries({ queryKey: ["formRequests"] });
    queryClient.invalidateQueries({ queryKey: ["formRequestsList"] });
    setResult(request);
    setView(request.status === "pending_ai" ? "personalizing" : "result");
  };

  const createMutation = useMutation({
    mutationFn: (params: Record<string, unknown>) => FormServices.createFormRequest(activeForm!.id, params as any),
    onSuccess: finish,
    onError: () => toast.error("We couldn't create the link. Try again."),
  });

  const saveMutation = useMutation({
    mutationFn: (params: Record<string, unknown>) =>
      FormServices.updateFormRequest(formRequest!.form?.id ?? activeForm!.id, formRequest!.id, params as any),
    onSuccess: request => {
      if (request.status === "pending_ai") return finish(request);
      queryClient.invalidateQueries({ queryKey: ["formRequests"] });
      queryClient.invalidateQueries({ queryKey: ["formRequestsList"] });
      toast.success("Link saved");
      onOpenChange(false);
    },
    onError: () => toast.error("We couldn't save the link. Try again."),
  });

  // AI personalization finishes in the background; check until the link is ready
  useEffect(() => {
    if (view !== "personalizing" || !result?.id) return;
    const check = async () => {
      try {
        const page = await FormServices.getFormRequests({ formRequestId: result.id } as any);
        const latest = page.content?.find(r => r.id === result.id);
        if (latest && latest.status !== "pending_ai") {
          setResult(latest);
          setView("result");
        }
      } catch {
        // A missed check is retried on the next tick
      }
    };
    const timer = setInterval(check, 3000);
    check();
    return () => clearInterval(timer);
  }, [view, result?.id]);

  const settingsParams = () => ({
    isTest,
    useAiPersonalization: allowAi && forClient ? useAi : false,
    ...(allowAi && forClient && useAi && aiContext.trim() ? { contextInstructions: aiContext.trim() } : {}),
  });

  const createLink = () => {
    const params: Record<string, unknown> = settingsParams();
    if (forClient && fan) params.fanId = fan.id;
    if ((forClient || audience === "player") && player) params.playerId = player.id;
    createMutation.mutate(params);
  };

  const saveLink = () => saveMutation.mutate(settingsParams());

  const handleFanCreated = async (data: any) => {
    setCreatingFan(true);
    try {
      const created = await FanServices.createFan({ ...data, playerId: data.playerId ?? player?.id, active: true });
      chooseFan(fanOption(created));
      if (!created.player && player) setPlayerFromFan(false);
      setNewFanOpen(false);
      toast.success(`${created.displayName || created.email || "Fan"} added to Fan Zone`);
    } catch {
      toast.error("We couldn't add the Fan. Try again.");
    } finally {
      setCreatingFan(false);
    }
  };

  const createAnother = () => {
    setView("compose");
    setResult(null);
    setUseAi(false);
    setAiContext("");
    setIsTest(false);
    if (!isFanZone) {
      setAudience("client");
      chooseFan(null);
    }
  };

  const openFormRoutes = () => {
    onOpenChange(false);
    history.push(`${URLS.coach.reputation.formRoutes}${activeForm?.guid ? `?form=${activeForm.guid}` : ""}`);
  };

  // What the link is for, in the header and the result summary
  const forWhom = fan?.displayName || fan?.email || (player ? player.name : null);
  const resultSummary = [
    fan ? `For ${fan.displayName || fan.email}` : audience === "anyone" ? "For anyone" : null,
    player ? `Player: ${player.name}` : forClient ? "No player" : null,
    result?.useAiPersonalization ? "AI personalized" : null,
    result?.isTest ? "Test link" : null,
  ].filter(Boolean);

  let title: string;
  let description: string | null;
  if (isEdit) {
    title = "Edit link";
    description = [
      formRequest?.form?.displayName ?? activeForm?.displayName,
      formRequest?.fan ? `for ${formRequest.fan.displayName || formRequest.fan.email}` : null,
      formRequest?.player ? `Player: ${formRequest.player.fullName}` : null,
    ]
      .filter(Boolean)
      .join(" · ");
  } else if (view !== "compose") {
    title = view === "result" ? "Link ready" : "Personalizing the link";
    description = [activeForm?.displayName, forWhom ? `for ${forWhom}` : null].filter(Boolean).join(" · ");
  } else if (isFanZone) {
    title = `Request feedback from ${lockedFan!.displayName || lockedFan!.email}`;
    description = "Pick a form and SocialCoach makes a personal link for them.";
  } else {
    title = "Create a form link";
    description = activeForm?.displayName ?? null;
  }

  const aiField = allowAi && (forClient || isEdit) && (
    <Field label="AI">
      <label className="flex items-start gap-2 text-sm sm:pt-2">
        <input
          type="checkbox"
          id="form-link-ai"
          className="mt-1 accent-primary"
          checked={useAi}
          onChange={e => setUseAi(e.target.checked)}
        />
        <span>
          Personalize the form for this client{" "}
          <Button variant="link" className="h-auto p-0 text-xs text-slate-400" onClick={() => setHelpOpen(true)}>
            How it works
          </Button>
        </span>
      </label>
      {useAi && (
        <Textarea
          id="form-link-ai-context"
          value={aiContext}
          onChange={e => setAiContext(e.target.value)}
          placeholder="Anything it should know? e.g. closed on a VA loan in 21 days (optional)"
          className="min-h-16 text-sm"
        />
      )}
    </Field>
  );

  // A test link is the one setting a link can have; on a player or general link it makes a separate link
  const testField = (
    <Field label="Test">
      <label className="flex items-start gap-2 text-sm sm:pt-2">
        <input
          type="checkbox"
          id="form-link-test"
          className="mt-1 accent-primary"
          checked={isTest}
          onChange={e => setIsTest(e.target.checked)}
        />
        <span>
          Make it a test link, so responses stay out of reporting
          {!forClient && !isEdit && isTest && (
            <span className="block text-xs text-slate-500">A test link is separate from the usual one.</span>
          )}
        </span>
      </label>
    </Field>
  );

  const routeNote = (
    <div className="flex gap-2.5 rounded-xl border border-blue-100 bg-blue-50/60 px-3 py-2.5 text-xs text-blue-900">
      <Info className="mt-0.5 size-3.5 shrink-0" />
      <span>
        Sending from a CRM or putting it on a website?{" "}
        <button type="button" className="font-semibold underline cursor-pointer" onClick={openFormRoutes}>
          Build a Form Route
        </button>{" "}
        so each client is recognized and reaches the right player.
      </span>
    </div>
  );

  const fanField = (
    <Field label="Client">
      {lockedFan ? (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
          <span className="font-semibold">{lockedFan.displayName || "No Name"}</span>
          {lockedFan.email && <span className="ml-1.5 text-xs text-slate-500">{lockedFan.email}</span>}
        </div>
      ) : newFanOpen ? (
        <div className="flex flex-col gap-2 rounded-xl bg-slate-50 p-3">
          <FanCreationForm
            onSubmit={handleFanCreated}
            isSubmitting={creatingFan}
            submitLabel="Add Fan"
            hidePlayerSelect={!!player}
            defaultValues={{
              displayName: "",
              email: "",
              phoneNumber: "",
              fanType: "customer",
              notes: "",
              playerId: player?.id || null,
            }}
          />
          <Button variant="ghost" size="sm" className="self-end" onClick={() => setNewFanOpen(false)}>
            Cancel
          </Button>
        </div>
      ) : (
        <>
          <Combobox
            {...fans}
            onValueChange={(value: FanOption | null, details?: { reason?: string }) => {
              if (!isProgrammaticClear(details)) chooseFan(value);
            }}
            placeholder="Search Fans by name or email"
            className="w-full"
          />
          <Button variant="link" className="h-auto self-start p-0 text-xs" onClick={() => setNewFanOpen(true)}>
            + New Fan
          </Button>
        </>
      )}
    </Field>
  );

  const playerHint =
    forClient && fan
      ? playerFromFan && player
        ? `From ${firstName(
            fan.displayName || "the Fan"
          )}'s Fan record. Change it if someone else should get the response.`
        : !player
        ? `${firstName(fan.displayName || "This Fan")} has no player yet. Pick one, or leave it empty.`
        : null
      : null;

  const playerField = (
    <Field label="Player">
      <Combobox
        {...players}
        onValueChange={(value: PlayerOption | null, details?: { reason?: string }) => {
          if (!isProgrammaticClear(details)) choosePlayer(value);
        }}
        placeholder="Search players by name or email"
        className="w-full"
      />
      {playerHint && <p className="text-xs text-slate-500">{playerHint}</p>}
    </Field>
  );

  const formField = (
    <Field label="Form">
      <Combobox {...forms} placeholder="Search forms" className="w-full" />
    </Field>
  );

  let body: React.ReactNode;
  let footer: React.ReactNode;
  const busy = createMutation.isPending || saveMutation.isPending;

  if (isEdit) {
    body = (
      <>
        <LinkBox url={formRequest!.url} caption="The link stays the same. Changes apply the next time it's opened." />
        {aiField}
        {testField}
      </>
    );
    footer = (
      <>
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button onClick={saveLink} loading={saveMutation.isPending}>
          Save
        </Button>
      </>
    );
  } else if (view === "personalizing" || view === "result") {
    body =
      view === "personalizing" ? (
        <div className="flex flex-col gap-2 rounded-xl bg-primary/5 p-3 text-sm text-slate-600" role="status">
          <div className="flex items-center justify-between gap-3">
            <span className="font-semibold text-slate-800">
              Personalizing for {firstName(fan?.displayName || forWhom || "your client")}…
            </span>
            <SocoAiLogo variant="color" height={16} className="shrink-0" />
          </div>
          <div className="h-1 overflow-hidden rounded-full bg-slate-200">
            <div className="h-full w-2/5 animate-pulse rounded-full bg-primary" />
          </div>
          <span>Usually under a minute.</span>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <span className="size-2.5 rounded-full bg-emerald-500" />
            {fan
              ? `${firstName(fan.displayName || fan.email || "Your client")}'s link is ready to send.`
              : "The link is ready."}
          </div>
          <LinkBox url={result!.url} />
        </>
      );
    body = (
      <>
        {body}
        {resultSummary.length > 0 && <p className="text-xs text-slate-500">{resultSummary.join(" · ")}</p>}
      </>
    );
    footer = (
      <>
        <Button variant="outline" className="sm:mr-auto" onClick={createAnother}>
          Create another
        </Button>
        <Button onClick={() => onOpenChange(false)}>Done</Button>
      </>
    );
  } else {
    const audienceSwitch = !isFanZone && (
      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-semibold text-slate-600">Who's it for?</span>
        <div
          className="inline-flex max-w-full self-start rounded-lg border border-slate-200 bg-slate-50 p-0.5"
          role="tablist"
          aria-label="Who's it for?"
        >
          {AUDIENCES.map(a => (
            <button
              key={a.id}
              type="button"
              role="tab"
              aria-selected={audience === a.id}
              onClick={() => switchAudience(a.id)}
              className={cn(
                "rounded-md px-3 py-1.5 text-sm font-semibold whitespace-nowrap cursor-pointer",
                audience === a.id ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
              )}
            >
              {a.label}
            </button>
          ))}
        </div>
      </div>
    );

    if (forClient) {
      body = (
        <>
          {audienceSwitch}
          {fanField}
          {isFanZone && formField}
          {fan && playerField}
          {fan && activeForm && aiField}
          {testField}
        </>
      );
      footer = (
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={createLink} loading={busy} disabled={!fan || !activeForm}>
            Create link
          </Button>
        </>
      );
    } else {
      const showPlain = !hasOptions && (audience === "anyone" || !!player);
      const caption =
        audience === "player" && player
          ? `${firstName(player.name)}'s link for this form. It's the same link every time, so responses stay together.`
          : `Anyone with this link can respond${
              activeForm?.fanIdentityCollection === "hidden" ? "" : " and is asked for their details"
            }. It's the same link every time.`;
      body = (
        <>
          {audienceSwitch}
          {audience === "player" && playerField}
          {showPlain &&
            (plainLink.data ? (
              <LinkBox url={plainLink.data.url} caption={caption} />
            ) : plainLink.isError ? (
              <p className="text-sm text-red-600">{plainLinkError(plainLink.error, player?.name)}</p>
            ) : (
              <p className="text-sm text-slate-400">Getting the link…</p>
            ))}
          {(audience === "anyone" || player) && testField}
          {routeNote}
        </>
      );
      footer = hasOptions ? (
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={createLink} loading={busy} disabled={audience === "player" && !player}>
            Create separate link
          </Button>
        </>
      ) : (
        <Button onClick={() => onOpenChange(false)}>Done</Button>
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="lg" showCloseButton>
      <Dialog.Header className="pr-12">
        <Dialog.Title className="text-base font-bold font-sans">{title}</Dialog.Title>
        {description && <Dialog.Description className="text-xs">{description}</Dialog.Description>}
      </Dialog.Header>

      <Dialog.Content className="flex max-h-[calc(100vh-16rem)] flex-col gap-4 overflow-y-auto px-6 py-4">
        {body}
      </Dialog.Content>

      <Dialog.Footer>{footer}</Dialog.Footer>

      <FormPersonalizationHelpDialog open={helpOpen} onOpenChange={setHelpOpen} />
    </Dialog>
  );
}
